import { useState, useEffect } from 'react';
import { BotsService } from '../services/bots.service';
import { AlertService } from '../services/alert.service';
import type { NodeData, Nodetype } from '../pages/campaings/flows/types';
import type { Node, Edge } from 'reactflow';

interface ApiNode {
  _id: string;
  type: Nodetype;
  name: string;
  is_root: boolean;
  position: { x: number; y: number };
  parent_nodes: Array<{ node: string; keywords: string[] }>;
  default?: any;
  http?: any;
  interactive?: any;
  redirect_agent?: any;
  close?: any;
}

type ApiResponse = Record<string, ApiNode>;

export function useFlowData(botId: string | undefined, isSandbox: number) {
  const [nodes, setNodes] = useState<Node<NodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!botId) return;
    setLoading(true);

    BotsService.getNodes({ bot_id: botId, is_sandox: isSandbox ? 1 : 0 })
      .then((res: ApiResponse) => {
        const loadedNodes: Node<NodeData>[] = Object.values(res).map((n) => ({
          id: n._id,
          type: n.type,
          position: { x: n.position.x, y: n.position.y },
          data: {
            title: n.name,
            type: n.type,
            meta: { isNew: false },
            isMain: n.is_root,
            _id: n._id,
            default: n.default,
            http: n.http,
            interactive: n.interactive,
            redirect_agent: n.redirect_agent,
            close: n.close,
            parent_nodes: n.parent_nodes.map((p) => ({ node: p.node, keywords: p.keywords }))
          } as NodeData
        }));

        const loadedEdges: Edge[] = [];
        loadedNodes.forEach((node) => {
          const parentList = node.data.parent_nodes || [];
          parentList.forEach(({ node: parentId, keywords }, idx) => {
            const kw = keywords || [];
            loadedEdges.push({
              id: `${parentId}-${node.id}-${idx}`,
              source: parentId,
              target: node.id,
              type: 'smoothstep',
              ...(kw.length > 0
                ? {
                    sourceHandle: kw[0],
                    label: kw.join(', '),
                    data: { keywords: kw }
                  }
                : {})
            });
          });
        });
        setNodes(loadedNodes);
        setEdges(loadedEdges);
      })
      .catch((err) => {
        console.error(err);
        AlertService.toastError(err.error || err);
      })
      .finally(() => setLoading(false));
  }, [botId, isSandbox]);

  return { nodes, edges, setNodes, setEdges, isLoading: loading };
}
